import type { WizardStepProps } from '../../../../types/builder.types'
import { getAggregatableFields, getAllFields } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { TableFieldsModal } from '../TableFieldsModal'
import { useMemo, useState } from 'react'
import {
  fieldsFromCustomQuery,
  getCustomQueryConfigSignature,
} from '../../../../utils/custom-query-builder-fields'

export function RadarChartConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const [showTableFields, setShowTableFields] = useState(false)
  const { dataSources } = useDataSources()

  const actualDataSource = config.dataSource === "custom_query" 
  ? config.customQuery?.config?.baseTable 
  : config.dataSource;

  const cqSig = useMemo(
    () => getCustomQueryConfigSignature(config.customQuery),
    [config.customQuery]
  )

  const { aggregatableFields, allFields } = useMemo(() => {
    if (!config.dataSource) return { aggregatableFields: [], allFields: [] }

    if (
      (config.dataSource === 'custom_query' ||
        (config.dataSource?.includes('ckan_data_generic') && config.customQuery)) &&
      config.customQuery?.config?.selectedFields?.length
    ) {
      const fieldsArray = fieldsFromCustomQuery(config.customQuery)
      return {
        aggregatableFields: fieldsArray.filter((f) => f.aggregatable),
        allFields: fieldsArray,
      }
    }

    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return {
        aggregatableFields: dynamicSource.fields.filter(f => f.aggregatable),
        allFields: dynamicSource.fields
      }
    }

    return {
      aggregatableFields: getAggregatableFields(config.dataSource),
      allFields: getAllFields(config.dataSource)
    }
  }, [dataSources, config.dataSource, cqSig])

  const radarConfig = config.radarChartConfig || { subjectKey: '', valueKeys: [] }
  const isValid = Boolean(
    config.metadata?.title &&
    radarConfig.subjectKey &&
    radarConfig.valueKeys?.length >= 3
  )

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-2">Configurar Gráfico Radial</h2>
      <p className="text-gray-600 mb-6">
        Compara múltiples dimensiones en un formato circular
      </p>

      <div className="space-y-6 bg-white border rounded-lg p-6">
         <div>
          {/* Botón o trigger para mostrar los campos */}
          <button
            onClick={() => setShowTableFields(true)}
            disabled={!config.dataSource}
            className="px-6 py-2 text-sm rounded-lg whitespace-nowrap transition-all border bg-blue-600 text-white border-blue-600 shadow-sm"
          >
            Ver campos disponibles para fuente de datos : <b>{actualDataSource}</b>
          </button>

          {/* Modal de campos (incluye fuentes BMP) */}
           {showTableFields && actualDataSource && ( 
              <TableFieldsModal
                dataSource={actualDataSource}
                componentType={config.componentType}
                dataSources={dataSources}
                onClose={() => setShowTableFields(false)}
              />
            )}
        </div>
        {/* Título */}
        <div>
          <label className="block font-medium mb-2">
            Título <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Perfil Provincial de Indicadores Forestales"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) =>
              onChange({
                ...config,
                metadata: { ...config.metadata, title: e.target.value },
              })
            }
          />
        </div>

        {/* Campo de sujeto (categoría a comparar) */}
        <div>
          <label className="block font-medium mb-2">
            Campo de Categoría (Sujeto) <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={radarConfig.subjectKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                radarChartConfig: { ...radarConfig, subjectKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {allFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Categoría principal a comparar (ej: provincia, especie, año)
          </p>
        </div>

        {/* Campos de valor (métricas) */}
        <div>
          <label className="block font-medium mb-2">
            Métricas/Dimensiones (mantén Ctrl para seleccionar varias){' '}
            <span className="text-red-500">*</span>
          </label>
          <select
            multiple
            size={6}
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={radarConfig.valueKeys || []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions).map(
                (opt) => opt.value
              )
              onChange({
                ...config,
                radarChartConfig: { ...radarConfig, valueKeys: selected },
              })
            }}
          >
            {aggregatableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Seleccionadas: {radarConfig.valueKeys?.length || 0} métricas
          </p>
          {radarConfig.valueKeys && radarConfig.valueKeys.length > 0 && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm font-medium text-blue-900 mb-1">
                Dimensiones seleccionadas:
              </p>
              <ul className="text-sm text-blue-800 space-y-1">
                {radarConfig.valueKeys.map((key) => {
                  const field = aggregatableFields.find((f) => f.name === key)
                  return (
                    <li key={key}>
                      • {field?.label || key}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
          {radarConfig.valueKeys && radarConfig.valueKeys.length < 3 && (
            <p className="text-sm text-red-600 mt-2">
              ⚠️ Se requieren al menos 3 métricas para un gráfico radial
            </p>
          )}
        </div>

        {/* Rango de valores */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-semibold text-yellow-900 mb-2">
            📏 Escala de Valores
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Valor Mínimo
              </label>
              <input
                type="number"
                placeholder="0"
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                value={radarConfig.minValue ?? ''}
                onChange={(e) =>
                  onChange({
                    ...config,
                    radarChartConfig: {
                      ...radarConfig,
                      minValue: e.target.value ? parseFloat(e.target.value) : undefined,
                    },
                  })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Valor Máximo
              </label>
              <input
                type="number"
                placeholder="100"
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                value={radarConfig.maxValue ?? ''}
                onChange={(e) =>
                  onChange({
                    ...config,
                    radarChartConfig: {
                      ...radarConfig,
                      maxValue: e.target.value ? parseFloat(e.target.value) : undefined,
                    },
                  })
                }
              />
            </div>
          </div>
          <p className="text-sm text-yellow-700 mt-2">
            💡 Deja en blanco para escala automática basada en los datos
          </p>
        </div>

        {/* Opciones de visualización */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="fillArea"
              checked={radarConfig.fillArea !== false}
              onChange={(e) =>
                onChange({
                  ...config,
                  radarChartConfig: { ...radarConfig, fillArea: e.target.checked },
                })
              }
              className="w-5 h-5"
            />
            <label htmlFor="fillArea" className="font-medium cursor-pointer">
              Rellenar área del gráfico
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="showDots"
              checked={radarConfig.showDots !== false}
              onChange={(e) =>
                onChange({
                  ...config,
                  radarChartConfig: { ...radarConfig, showDots: e.target.checked },
                })
              }
              className="w-5 h-5"
            />
            <label htmlFor="showDots" className="font-medium cursor-pointer">
              Mostrar puntos en los valores
            </label>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={onPrev}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300"
        >
          ← Atrás
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Vista Previa →
        </button>
      </div>
    </div>
  )
}
